import { useEffect } from "react";
import { FiInfo, FiAlertTriangle, FiCheckCircle, FiX } from "react-icons/fi";
import "./inline-notice.style.scss";

interface InlineNoticeProps {
	message: string | null;
	type?: "info" | "warning" | "success";
	autoHideMs?: number;
	onDismiss?: () => void;
}

const ICONS = {
	info: FiInfo,
	warning: FiAlertTriangle,
	success: FiCheckCircle,
};

// Inline replacement for showToastMessage(msg, "I"/"W"/"S") — for one-shot,
// non-mutation-bound nudges (client-side validation, bulk-selection feedback)
// and for success confirmations in views where a result needs to flash
// briefly alongside content that stays on screen (e.g. a list), where
// SuccessDisplay's full-block replacement would be wrong. Caller owns the
// message state; this just renders it. No auto-hide by default — pass
// autoHideMs to opt in — the dismiss button always lets the user close it.
export default function InlineNotice({
	message,
	type = "info",
	autoHideMs,
	onDismiss,
}: InlineNoticeProps) {
	useEffect(() => {
		if (!message || !autoHideMs) return;
		const timer = setTimeout(() => onDismiss?.(), autoHideMs);
		return () => clearTimeout(timer);
	}, [message, autoHideMs, onDismiss]);

	if (!message) return null;

	const Icon = ICONS[type];

	return (
		<div className={`inline-notice inline-notice--${type}`}>
			<Icon size={16} aria-hidden="true" />
			<p>{message}</p>
			{onDismiss && (
				<button
					type="button"
					className="inline-notice-dismiss"
					aria-label="Dismiss"
					onClick={onDismiss}
				>
					<FiX size={16} aria-hidden="true" />
				</button>
			)}
		</div>
	);
}
